import _defaultTo from 'lodash/defaultTo';
import _filter from 'lodash/filter';
import _find from 'lodash/find';
import _forEach from 'lodash/forEach';
import _get from 'lodash/get';
import _orderBy from 'lodash/orderBy';
import _without from 'lodash/without';
// Permissions
import { deserialize, serialize } from 'serializr';
// Serialization
import { parkingCategorySchema } from './schemas';
// Fragments
import {
  parkingCategoryFragment, parkingCategoryForDashboardFragment, parkingCategoryForOnDemandAccessesFragment, operationFragment,
} from './fragments';
// Custom
import { ONEPARK_COLOR } from '../../constants/App';
import { carparkSpacesTypePalette } from '../../components/core/colors';

/**
* Serialization
*/
const deserializeParkingCategory = (data) => deserialize(parkingCategorySchema, data);

const serializeParkingCategory = ({
  name, color, defaultAllotment,
}) => ({
  ...serialize(parkingCategorySchema, {
    color: _defaultTo(color, null),
    name: _defaultTo(name, null),
    defaultAllotment: _defaultTo(defaultAllotment, null),
  }),
});

/**
* Lists
*/
const parkingCategoryTypesList = [
  {
    key: 'onDemand',
    value: 'on_demand',
  },
  {
    key: 'regular',
    value: 'regular',
  },
];

/**
* Utils
*/
const sortParkingCategories = (parkingCategories, fields = ['name'], dirs = ['asc']) => (_orderBy(parkingCategories, fields, dirs));
const sortRegularParkingCategories = (parkingCategories, fields = ['name'], dirs = ['asc']) => (_filter(
  sortParkingCategories(parkingCategories, fields, dirs), ['type', parkingCategoryTypesList[1].value],
));

const excludeCurrentParkingCategory = (parkingCategories, parkingCategory) => (_without(parkingCategories, parkingCategory));
const parkingCategoryTitle = (parkingCategoryData) => _get(parkingCategoryData, 'name', '');
const parkingCategoryColor = (parkingCategoryData) => (parkingCategoryData.isOnepark ? ONEPARK_COLOR : parkingCategoryData.color);
const parkingCategoryGetAssociatedPriceGrid = (parkingCategoryData) => _get(parkingCategoryData, 'priceGridAssociation.priceGrid');
const getParkingCategoryById = (parkingCategories, parkingCategoryId) => _find(parkingCategories, { id: +parkingCategoryId });

const isRegular = (parkingCategoryData) => {
  const type = _get(parkingCategoryData, 'type');
  return type === parkingCategoryTypesList[1].value;
};
const isAssignedOnly = (parkingCategoryData) => isRegular(parkingCategoryData) && _get(parkingCategoryData, 'defaultAllotment.bookingCapacities.max') === 0 && _get(parkingCategoryData, 'defaultAllotment.withoutBookingCapacities.max') === 0;

const parkingCategoryConfiguration = {
  bookingMax: {
    labelId: 'field.parkingCategoryMaximumBooking',
    color: carparkSpacesTypePalette.bookings,
  },
  bookingMin: {
    labelId: 'field.parkingCategoryGuaranteedBooking',
    color: carparkSpacesTypePalette.bookings,
  },
  assigned: {
    labelId: 'field.parkingCategorySpacesAssigned',
    color: carparkSpacesTypePalette.assigned,
  },
  withoutBookingMax: {
    labelId: 'field.parkingCategoryMaximumWithoutBooking',
    color: carparkSpacesTypePalette.withoutBooking,
  },
  withoutBookingMin: {
    labelId: 'field.parkingCategoryGuaranteedWithoutBooking',
    color: carparkSpacesTypePalette.withoutBooking,
  },
};

const parkingCategoryPredefinedColors = [
  '#1e88e5',
  '#fdd835',
  '#43a047',
  '#d81b60',
  '#c0ca33',
  '#fb8c00',
  '#8e24aa',
  '#795548',
];

const getOnDemandParkingCategory = (parkingCategories) => _find(parkingCategories, ['type', parkingCategoryTypesList[0].value]);
const parkingCategoryIsOnDemand = (parkingCategoryData) => _get(parkingCategoryData, 'type', '').toLowerCase() === parkingCategoryTypesList[0].value;

const isWarningBooking = (parkingCategories, parkingCategory, max, bookingCapacity) => {
  const excludeCurrentCategory = excludeCurrentParkingCategory(parkingCategories, parkingCategory);
  let sumMinimum = 0;

  _forEach(excludeCurrentCategory, (element) => {
    sumMinimum += element.defaultAllotment.bookingCapacities.min;
  });

  return {
    isWarning: max > bookingCapacity - sumMinimum,
    capacities: bookingCapacity - sumMinimum,
  };
};

const isWarningWithout = (parkingCategories, parkingCategory, max, withoutBookingCapacity) => {
  const excludeCurrentCategory = excludeCurrentParkingCategory(parkingCategories, parkingCategory);
  let sumMinimum = 0;

  _forEach(excludeCurrentCategory, (element) => {
    sumMinimum += element.defaultAllotment.withoutBookingCapacities.min;
  });

  return {
    isWarning: max > withoutBookingCapacity - sumMinimum,
    capacities: withoutBookingCapacity - sumMinimum,
  };
};

/**
* GraphQL
*/
const queListParkingCategories = () => `
  query ListParkingCategories($input: ListParkingCategoriesInput!, $search: ListParkingCategoriesSearchInput) {
    listParkingCategories(input: $input, search: $search) {
      ...parkingCategoryFragment
    }
  }

  ${parkingCategoryFragment}
`;

const queListParkingCategoriesForDashboard = `
  query ListParkingCategoriesForDashboard($input: ListParkingCategoriesInput!, $search: ListParkingCategoriesSearchInput) {
    listParkingCategories(input: $input, search: $search) {
      ...parkingCategoryForDashboardFragment
    }
  }

  ${parkingCategoryForDashboardFragment}
`;
const queListParkingCategoriesForOnDemandAccesses = `
  query ListParkingCategoriesForDashboard($input: ListParkingCategoriesInput!, $search: ListParkingCategoriesSearchInput) {
    listParkingCategories(input: $input, search: $search) {
      ...parkingCategoryForOnDemandAccessesFragment
    }
  }

  ${parkingCategoryForOnDemandAccessesFragment}
`;

const queGetParkingCategory = () => `
  query GetParkingCategory($input: GetParkingCategoryInput!) {
    getParkingCategory(input: $input) {
      ...parkingCategoryFragment
    }
  }

  ${parkingCategoryFragment}
`;

const mutAddParkingCategory = `
  mutation AddParkingCategory($input: AddParkingCategoryInput!) {
    addParkingCategory(input: $input) {
      id,
      operation {
        ...operationFragment
      }
    }
  }

  ${operationFragment}
`;

const mutUpdateParkingCategory = `
  mutation UpdateParkingCategory($input: UpdateParkingCategoryInput!) {
    updateParkingCategory(input: $input) {
      id,
      operation {
        ...operationFragment
      }
    }
  }

  ${operationFragment}
`;

const mutDeleteParkingCategory = `
  mutation DeleteParkingCategory($input: DeleteParkingCategoryInput!) {
    deleteParkingCategory(input: $input) {
      id,
      operation {
        ...operationFragment
      }
    }
  }

  ${operationFragment}
`;

const mutAddPriceGridToParkingCategory = `
  mutation AddPriceGridToParkingCategory($input: AddPriceGridToParkingCategoryInput!) {
    addPriceGridToParkingCategory(input: $input) {
      ...operationFragment
    }
  }

  ${operationFragment}
`;

const mutDeletePriceGridFromParkingCategory = `
  mutation DeletePriceGridFromParkingCategory($input: DeletePriceGridFromParkingCategoryInput!) {
    deletePriceGridFromParkingCategory(input: $input) {
      ...operationFragment
    }
  }

  ${operationFragment}
`;

const mutSendPriceGridToCustomer = `
  mutation SendPriceGridToCustomer($input: SendPriceGridToCustomerInput!) {
    sendPriceGridToCustomer(input: $input) {
      id
    }
  }
`;

/**
* Model
*/
const parkingCategory = {
  // Name
  name: 'parkingCategory',

  // Serialization
  deserialize: deserializeParkingCategory,
  serialize: serializeParkingCategory,

  // Sort elements
  sortElements: sortParkingCategories,
  sortRegularElements: sortRegularParkingCategories,
  excludeCurrentCategory: excludeCurrentParkingCategory,

  // List
  configuration: parkingCategoryConfiguration,

  // Utils
  title: parkingCategoryTitle,
  color: parkingCategoryColor,
  getAssociatedPriceGrid: parkingCategoryGetAssociatedPriceGrid,
  predefinedColors: parkingCategoryPredefinedColors,
  getOnDemand: getOnDemandParkingCategory,
  isOnDemand: parkingCategoryIsOnDemand,
  isWarningBooking,
  isWarningWithout,
  getParkingCategoryById,
  isAssignedOnly,

  // GraphQL
  queListParkingCategories,
  queListParkingCategoriesForDashboard,
  queListParkingCategoriesForOnDemandAccesses,
  queGetParkingCategory,
  mutAddParkingCategory,
  mutUpdateParkingCategory,
  mutDeleteParkingCategory,
  mutAddPriceGridToParkingCategory,
  mutDeletePriceGridFromParkingCategory,
  mutSendPriceGridToCustomer,
};

module.exports = parkingCategory;



// WEBPACK FOOTER //
// ./models/cpm/parkingCategory.js